import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/boards`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const chan = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('ops-boards') : null;

export function useBoards() {
  const [boards, setBoards] = useState(null);
  const [error, setError] = useState('');
  const boardsRef = useRef(null);
  const timers = useRef({});
  const dirty = useRef(new Set()); // board ids with local edits not yet acknowledged — a refresh must not clobber them
  const mine = useRef(Math.random());
  useEffect(() => { boardsRef.current = boards; }, [boards]);

  const refresh = useCallback(async () => {
    if (document.querySelector('.react-draggable-dragging, .react-grid-item.resizing')) return;
    try {
      const { data } = await axios.get(API, auth());
      setBoards(bs => data.boards.map(b => (dirty.current.has(b.id) && bs) ? (bs.find(x => x.id === b.id) || b) : b));
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load your boards');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // other windows (your other monitors) tell us when a board changed
  useEffect(() => {
    if (!chan) return;
    const onMsg = (ev) => { if (ev.data?.type === 'changed' && ev.data.src !== mine.current) refresh(); };
    chan.addEventListener('message', onMsg);
    const t = setInterval(refresh, 45000);
    return () => { chan.removeEventListener('message', onMsg); clearInterval(t); };
  }, [refresh]);

  const announce = () => chan?.postMessage({ type: 'changed', src: mine.current });
  const put = (board) => setBoards(bs => (bs || []).map(b => b.id === board.id ? { ...b, ...board } : b));

  // board-level settings (name, cols, compact): server copy wins
  const patch = useCallback(async (id, body) => {
    const { data } = await axios.patch(`${API}/${id}`, body, auth());
    setBoards(bs => (bs || []).map(b => b.id === data.id ? { ...b, ...data, layout: dirty.current.has(id) ? b.layout : data.layout, widgets: dirty.current.has(id) ? b.widgets : data.widgets } : b));
    announce();
    return data;
  }, []);

  // layout + widget configs: local state is the truth; the server just gets told (debounced)
  const flush = useCallback((id) => {
    const b = (boardsRef.current || []).find(x => x.id === id);
    if (!b) return;
    dirty.current.delete(id);
    axios.patch(`${API}/${id}`, { layout: b.layout, widgets: Object.fromEntries(Object.entries(b.widgets || {}).map(([k, w]) => [k, { config: w.config || {} }])) }, auth())
      .then(announce).catch(() => {});
  }, []);
  const schedule = useCallback((id, ms) => {
    dirty.current.add(id);
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => flush(id), ms);
  }, [flush]);

  const saveLayout = useCallback((id, layout) => {
    setBoards(bs => (bs || []).map(b => b.id === id ? { ...b, layout } : b));
    schedule(id, 600);
  }, [schedule]);

  const updateConfig = useCallback((id, wid, config) => {
    setBoards(bs => (bs || []).map(b => b.id === id ? { ...b, widgets: { ...b.widgets, [wid]: { ...b.widgets[wid], config: { ...(b.widgets[wid]?.config || {}), ...config } } } } : b));
    schedule(id, 500);
  }, [schedule]);

  // flush pending edits when the page is left
  useEffect(() => {
    const onHide = () => { for (const id of Array.from(dirty.current)) { clearTimeout(timers.current[id]); flush(id); } };
    window.addEventListener('pagehide', onHide);
    return () => { onHide(); window.removeEventListener('pagehide', onHide); };
  }, [flush]);

  const addWidget = useCallback(async (id, type, config, size) => {
    const { data } = await axios.post(`${API}/${id}/widgets`, { type, config, ...size }, auth());
    put(data.board); announce();
    return data.widget_id;
  }, []);

  const removeWidget = useCallback(async (id, wid) => {
    const { data } = await axios.delete(`${API}/${id}/widgets/${wid}`, auth());
    put(data); announce();
  }, []);

  const moveWidget = useCallback(async (id, wid, toId) => {
    const { data } = await axios.post(`${API}/${id}/widgets/${wid}/move`, { to_board_id: toId }, auth());
    put(data.from); put(data.to); announce();
  }, []);

  const createBoard = useCallback(async (name) => {
    const { data } = await axios.post(API, { name }, auth());
    setBoards(bs => [...(bs || []), data]); announce();
    return data;
  }, []);

  const deleteBoard = useCallback(async (id) => {
    await axios.delete(`${API}/${id}`, auth());
    setBoards(bs => (bs || []).filter(b => b.id !== id)); announce();
  }, []);

  const reset = useCallback(async () => {
    const { data } = await axios.post(`${API}/reset`, {}, auth());
    dirty.current.clear();
    setBoards(data.boards); announce();
  }, []);

  const reorder = useCallback(async (ids) => {
    setBoards(bs => ids.map(i => (bs || []).find(b => b.id === i)).filter(Boolean));
    await axios.post(`${API}/reorder`, { ids }, auth()); announce();
  }, []);

  return { boards, error, refresh, patch, saveLayout, updateConfig, addWidget, removeWidget, moveWidget, createBoard, deleteBoard, reset, reorder };
}
